import { EllipsisVertical } from "lucide-react-native";
import { FC, ReactNode } from "react";
import { GestureResponderEvent } from "react-native";

import {
  HStack,
  Menu,
  MenuItem,
  MenuItemLabel,
  MenuSeparator,
  Pressable,
} from "@/components/ui";
import { Action } from "@/types/utils";

interface ActionMenuProps {
  actions: Action[];
  onOpen?: (e: GestureResponderEvent) => void;
  trigger?: (triggerProps: Record<string, unknown>) => ReactNode;
}

const ActionMenu: FC<ActionMenuProps> = ({ actions, onOpen, trigger }) => {
  const menuChildren: ReactNode[] = [];
  actions.forEach((action, index) => {
    if (action.divider && index > 0) {
      menuChildren.push(
        <MenuSeparator className="my-1" key={`sep-${action.label}`} />
      );
    }
    const Icon = action.icon;
    menuChildren.push(
      <MenuItem
        className={`px-3 py-2 rounded-lg hover:bg-outline-100 dark:hover:bg-outline-800 ${action.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
        disabled={action.disabled}
        key={`item-${action.label}`}
        onPress={action.onPress}
        textValue={action.label}
        onKeyDown={(e) => {
          if (e.nativeEvent.key === "Enter") {
            e.preventDefault();
            if (!action.disabled) action.onPress?.();
          }
        }}
      >
        <HStack className="items-center gap-2">
          <Icon className={`text-${action.color}-600`} size={14} />
          <MenuItemLabel
            className={`text-sm font-medium text-${action.color}-600`}
          >
            {action.label}
          </MenuItemLabel>
        </HStack>
      </MenuItem>
    );
  });

  return (
    <Menu
      className="min-w-[160px] bg-white dark:bg-outline-900 border border-outline-200 dark:border-outline-700 rounded-xl shadow-lg p-1.5"
      trigger={({ ...triggerProps }) => {
        if (trigger) {
          return trigger(triggerProps as Record<string, unknown>);
        }
        return (
          <Pressable
            {...triggerProps}
            className="p-1.5 rounded-lg hover:bg-background-100"
            onPress={(e) => {
              e.stopPropagation();
              onOpen?.(e);
              (
                triggerProps as { onPress?: (e: GestureResponderEvent) => void }
              ).onPress?.(e);
            }}
          >
            <EllipsisVertical className="text-typography-400" size={16} />
          </Pressable>
        );
      }}
    >
      {menuChildren}
    </Menu>
  );
};

export default ActionMenu;
